import { useQuery } from "@tanstack/react-query";

import { canUsePermission } from "../../../lib/auth";
import { useCurrentAuthUser } from "../../auth";
import {
  canRetryItineraryPreviewLoad,
  ITINERARY_PERMISSIONS,
  itineraryPreviewLoadErrorText,
  itineraryPreviewRuntime,
  ItineraryOutputDrawer,
} from "../../itinerary";
import "../../itinerary/ui/itineraryEditor.css";

type Props = {
  itineraryId: string | null;
  /** 单行程直开时，ID 尚未解析完也先展示加载抽屉。 */
  resolving?: boolean;
  onClose: () => void;
};

/** 在定制列表内打开统一行程预览抽屉，不跳转到课程线路菜单页。 */
export function CustomizationItineraryPreviewHost({
  itineraryId,
  resolving = false,
  onClose,
}: Props) {
  const currentUserQuery = useCurrentAuthUser();
  const previewQuery = useQuery({
    queryKey: ["itinerary", "preview", itineraryId] as const,
    queryFn: () => itineraryPreviewRuntime.getEditor(itineraryId ?? ""),
    enabled: Boolean(itineraryId),
    retry: false,
  });
  const canExport = currentUserQuery.isSuccess
    && canUsePermission(currentUserQuery.data, ITINERARY_PERMISSIONS.export);
  const canShare = currentUserQuery.isSuccess
    && canUsePermission(currentUserQuery.data, ITINERARY_PERMISSIONS.share);
  const previewDegraded = !previewQuery.isError && previewQuery.data?.previewDegraded === true;
  const previewErrorMessage = previewQuery.isError
    ? itineraryPreviewLoadErrorText(previewQuery.error)
    : undefined;
  const previewCanRetry = previewQuery.isError
    && canRetryItineraryPreviewLoad(previewQuery.error);
  const visible = Boolean(itineraryId) || resolving;

  return (
    <ItineraryOutputDrawer
      visible={visible}
      editor={previewQuery.isError ? undefined : previewQuery.data?.payload}
      loading={resolving || (Boolean(itineraryId) && previewQuery.isLoading)}
      errorMessage={resolving ? undefined : previewErrorMessage}
      onRetry={previewCanRetry ? () => void previewQuery.refetch() : undefined}
      canExport={!resolving && canExport && !previewDegraded && !previewQuery.isError}
      canShare={!resolving && canShare && !previewQuery.isError}
      enableInternalInteractions={!resolving}
      warningMessage={
        resolving || !previewDegraded
          ? undefined
          : `该历史行程有 ${previewQuery.data?.previewSkippedSegmentCount ?? 0} 个交通段因缺少可验证的节点引用未展示；分享将使用可确认的历史内容，后台导出暂不可用。`
      }
      onClose={onClose}
    />
  );
}
